/**
 * The rendered width of a run of text at a given type, without a reflow.
 *
 * One canvas measures every caller; the page's body font family is read
 * once so the measurement matches what the page paints. Where there is no
 * canvas (a node test), the estimate is a wide average glyph advance so a
 * layout that reserves the width errs toward room, never toward a clipped
 * label. Results are cached by text and type.
 */
export type TextMetrics = {
  /** Font size in px. */
  sizePx: number;
  /** CSS font-weight; 400 when omitted. */
  weight?: number;
  /** Letter spacing in px, added per glyph; 0 when omitted. */
  letterSpacingPx?: number;
};

/** The widest plausible average advance for proportional type, as a share of
 *  the font size — the fallback where no canvas can measure. */
const FALLBACK_ADVANCE_EM = 0.55;

let context: CanvasRenderingContext2D | null | undefined;
let bodyFamily: string | undefined;
const cache = new Map<string, number>();

function canvasContext(): CanvasRenderingContext2D | null {
  if (context !== undefined) return context;
  if (typeof document === "undefined") {
    context = null;
    return context;
  }
  context = document.createElement("canvas").getContext("2d");
  return context;
}

function fontFamily(): string {
  bodyFamily ??=
    typeof document !== "undefined" && document.body
      ? getComputedStyle(document.body).fontFamily || "sans-serif"
      : "sans-serif";
  return bodyFamily;
}

export function measureTextWidth(text: string, metrics: TextMetrics): number {
  const { sizePx, weight = 400, letterSpacingPx = 0 } = metrics;
  const key = `${sizePx}|${weight}|${letterSpacingPx}|${text}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  const ctx = canvasContext();
  let width: number;
  if (ctx) {
    ctx.font = `${weight} ${sizePx}px ${fontFamily()}`;
    width = ctx.measureText(text).width + letterSpacingPx * text.length;
  } else {
    width = text.length * (sizePx * FALLBACK_ADVANCE_EM + letterSpacingPx);
  }
  cache.set(key, width);
  return width;
}
